import React, {
  Suspense,
  ReactNode,
  useRef,
  useEffect,
  useState,
  useLayoutEffect,
  memo,
  lazy,
} from 'react';
import { Canvas, useThree, useLoader } from '@react-three/fiber';
import { Html, OrbitControls } from '@react-three/drei';
import { GLTFLoader, DRACOLoader } from 'three-stdlib';
import * as THREE from 'three';
import { useInView } from 'react-intersection-observer';
import { API_BASE_URL } from '../../constants/config';
import DisplayClickableItem from './DisplayClickableItem';

interface ErrorBoundaryProps {
  children: ReactNode;
}
interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    this.setState({ hasError: true });
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }
  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Html center>
          <div className="text-white text-center bg-black/70 p-4 rounded">
            Error loading 3D model.
          </div>
        </Html>
      );
    }
    return this.props.children;
  }
}

const Model: React.FC = memo(() => {
  const gltf = useLoader(
    GLTFLoader,
    `${API_BASE_URL}/api/assembly/assembly_draco.glb`,
    (loader: GLTFLoader) => {
      const dracoLoader = new DRACOLoader();
      dracoLoader.setDecoderPath(
        'https://www.gstatic.com/draco/versioned/decoders/1.5.7/'
      );
      loader.setDRACOLoader(dracoLoader);
    }
  );

  const ref = useRef<THREE.Group>(null);
  const { scene } = useThree();

  useLayoutEffect(() => {
    if (!ref.current) return;
    const object = ref.current;

    const recenter = () => {
      const box = new THREE.Box3().setFromObject(object);
      const center = box.getCenter(new THREE.Vector3());
      object.position.sub(center);
      object.scale.set(2, 2, 2);
      object.rotation.x = -Math.PI / 2;
      object.rotation.y = 0;
    };

    recenter();
    requestAnimationFrame(recenter);
  }, [gltf, scene]);

  return <primitive ref={ref} object={gltf.scene} />;
});

const Scene = ({
  controlsRef,
  selectedSensorKey,
}: {
  controlsRef: any;
  selectedSensorKey?: string | null;
}) => {
  const { camera } = useThree();
  const [blink, setBlink] = useState(true);

  const sensorBoxes = [
    {
      key: 'GNSS',
      position: [0.88, 0.15, 0.619],
      size: [0.3, 0.2, 0.3],
      message: 'Multi-band Emlid Reach M2',
    },
    {
      key: 'GNSS',
      position: [-0.89, 0.15, 1.1],
      size: [0.3, 0.2, 0.3],
      message: 'Multi-band Emlid Reach M2',
    },
    {
      key: 'GNSS',
      position: [-0.89, 0.15, -1.1],
      size: [0.3, 0.2, 0.3],
      message: 'Multi-band Emlid Reach M2',
    },
    {
      key: 'GNSS',
      position: [0.89, 0.05, -0.7],
      size: [0.18, 0.28, 0.18],
      message: 'Single-band Emlid Reach M2',
    },
    {
      key: 'GNSS',
      position: [-0.89, 0.05, -0.82],
      size: [0.18, 0.28, 0.18],
      message: 'Single-band Emlid Reach M2',
    },
    {
      key: 'GNSS',
      position: [-0.89, 0.05, 0.75],
      size: [0.18, 0.28, 0.18],
      message: 'Single-band Emlid Reach M2',
    },
  ];

  useEffect(() => {
    if (!selectedSensorKey) return;
    const interval = setInterval(() => setBlink((prev) => !prev), 1500);
    return () => clearInterval(interval);
  }, [selectedSensorKey]);

  useEffect(() => {
    if (!selectedSensorKey || !controlsRef.current) return;

    const sensorViews: Record<string, [number, number, number]> = {
      ZedX: [2, 0, -0.1],
      Basler: [-2.5, 0, 0],
      Navtech: [-2, 1, 0],
      RoboSense: [2, 0.5, -0.1],
      Leishen: [2, -0.7, 0],
      VN100: [-0.8, -0.2, -1.8],
      'XSens Mti30': [-0.8, -0.2, -1.8],
      GNSS: [2.5, 2.5, 2.5],
    };

    const targetPos = sensorViews[selectedSensorKey];
    if (!targetPos) return;

    const duration = 0.8;
    const start = camera.position.clone();
    const end = new THREE.Vector3(...targetPos);
    const startTime = performance.now();

    const animate = (time: number) => {
      const t = Math.min((time - startTime) / (duration * 1000), 1);
      camera.position.lerpVectors(start, end, t);
      camera.lookAt(0, 0, 0);
      controlsRef.current.update();
      if (t < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [selectedSensorKey, camera, controlsRef]);

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={0.6} />

      <ErrorBoundary>
        <Suspense
          fallback={
            <Html center>
              <div className="text-white text-center bg-black/70 p-4 rounded">
                Loading 3D model...
              </div>
            </Html>
          }
        >
          <Model />
          {sensorBoxes.map((sensor, index) => {
            const isActive = sensor.key === selectedSensorKey;
            const visible = isActive ? blink : false;

            return (
              <DisplayClickableItem
                key={`${sensor.key}-${index}`}
                position={sensor.position as [number, number, number]}
                width={sensor.size[0]}
                height={sensor.size[1]}
                depth={sensor.size[2]}
                message={sensor.message}
                visible={visible}
              />
            );
          })}
        </Suspense>
      </ErrorBoundary>

      <OrbitControls
        ref={controlsRef}
        enableDamping
        dampingFactor={0.05}
        minDistance={0.5}
        maxDistance={10}
        enablePan
        enableZoom
        enableRotate
      />
    </>
  );
};

const AssemblyModel: React.FC<{ selectedSensorKey?: string | null }> = ({
  selectedSensorKey,
}) => {
  const controlsRef = useRef<any>(null);
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });
  const [ready, setReady] = useState(false);

  const resetCameraView = () => {
    if (controlsRef.current) controlsRef.current.reset();
  };

  useEffect(() => {
    if (inView) {
      const t = setTimeout(() => setReady(true), 250);
      return () => clearTimeout(t);
    }
  }, [inView]);

  return (
    <div ref={ref} className="relative w-full max-w-4xl mx-auto h-[50vh]">
      {ready ? (
        <Canvas camera={{ position: [2.5, 2.5, 2.5], fov: 45 }}>
          <Scene
            controlsRef={controlsRef}
            selectedSensorKey={selectedSensorKey}
          />
        </Canvas>
      ) : (
        <div className="flex items-center justify-center h-full text-gray-400 animate-pulse">
          Initializing 3D viewer…
        </div>
      )}

      {ready && (
        <button
          onClick={resetCameraView}
          className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700 flex items-center justify-center"
        >
          Reset Camera
        </button>
      )}
    </div>
  );
};

export default AssemblyModel;
